"use client"

import { useState, useEffect } from "react"
import { Input } from "@/components/ui/input"
import { toast } from "sonner"
import { useLanguage } from "@/lib/i18n/language-provider"

// Fonction pour évaluer une expression mathématique et arrondir à l'entier supérieur
const evaluateMathExpression = (expression: string): number | null => {
  try {
    // Nettoyer l'expression pour éviter les injections de code malveillant
    const sanitizedExpression = expression
      .replace(/[^0-9+\-*/().]/g, ''); // Ne garder que les chiffres et opérateurs mathématiques (** inclus pour l'exponentiation)

    // Évaluer l'expression
    const result = new Function(`return ${sanitizedExpression}`)();
    
    // Vérifier que le résultat est un nombre valide
    if (typeof result !== 'number' || !isFinite(result)) {
      return null;
    }
    
    // Arrondir à l'entier supérieur
    return Math.ceil(result);
  } catch (error) {
    // En cas d'erreur d'évaluation, retourner null
    return null;
  }
};

// Composant pour les champs numériques avec évaluation mathématique
interface MathInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  value: number;
  onValueChange: (value: number) => void;
}

export const MathInput = ({ value, onValueChange, ...props }: MathInputProps) => {
  const { t } = useLanguage();
  const [inputValue, setInputValue] = useState(value.toString());

  // Mettre à jour inputValue si la valeur change depuis l'extérieur
  useEffect(() => {
    setInputValue(value.toString());
  }, [value]);

  // Gérer le changement de valeur dans l'input
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  // Gérer l'appui sur la touche Entrée
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const result = evaluateMathExpression(inputValue);
      if (result !== null) {
        onValueChange(result);
        setInputValue(result.toString()); // Mettre à jour l'affichage avec le résultat
      } else {
        toast.error(props['aria-label'] ? `${props['aria-label']}: ${t("multigrid.invalidCalculation")}` : t("multigrid.invalidCalculation"));
      }
    }
  };

  // Gérer la perte de focus
  const handleBlur = () => {
    const result = evaluateMathExpression(inputValue);
    if (result !== null) {
      onValueChange(result);
      setInputValue(result.toString());
    } else {
      // Si l'expression est invalide, revenir à la valeur précédente
      setInputValue(value.toString());
    }
  };

  return (
    <Input
      {...props}
      type="text"
      value={inputValue}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
    />
  );
}; 